import React from 'react';
import styled from 'styled-components';

export type InspectorPanelProps = {
  title?: string;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
};

export type InspectorSectionProps = {
  title?: string;
  description?: React.ReactNode;
  trailing?: React.ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
  children: React.ReactNode;
  className?: string;
};

const Root = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--color-surface);
  border-left: 1px solid var(--color-border-subtle);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-4) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Subtitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin-top: 2px;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border-strong) transparent;
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const FooterArea = styled.div`
  border-top: 1px solid var(--color-border-subtle);
  padding: var(--space-3) var(--space-4) var(--space-4);
  background: var(--color-surface-subtle);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const SectionRoot = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const SectionHead = styled.button<{ $clickable: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  text-align: left;
  width: 100%;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  color: var(--color-text);
`;

const SectionTitle = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const SectionDescription = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  line-height: var(--line-height-base);
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-block;
  transform: rotate(${(p) => (p.$open ? '0deg' : '-90deg')});
  transition: transform var(--duration-fast) var(--ease-standard);
  color: var(--color-text-tertiary);
  font-size: 10px;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

export const InspectorSection: React.FC<InspectorSectionProps> = ({
  title,
  description,
  trailing,
  defaultOpen = true,
  collapsible = false,
  children,
  className,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const isOpen = collapsible ? open : true;

  return (
    <SectionRoot className={className}>
      {(title || description || trailing || collapsible) && (
        <SectionHead
          type="button"
          $clickable={collapsible}
          onClick={() => collapsible && setOpen((v) => !v)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {title && <SectionTitle>{title}</SectionTitle>}
            {description && <SectionDescription>{description}</SectionDescription>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {trailing}
            {collapsible && <Caret $open={open}>▾</Caret>}
          </div>
        </SectionHead>
      )}
      {isOpen && <SectionBody>{children}</SectionBody>}
    </SectionRoot>
  );
};

InspectorSection.displayName = 'InspectorSection';

export const InspectorPanel: React.FC<InspectorPanelProps> = ({
  title,
  subtitle,
  trailing,
  children,
  footer,
  className,
}) => {
  return (
    <Root className={className}>
      {(title || subtitle || trailing) && (
        <Head>
          <div>
            {title && <Title>{title}</Title>}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
          <div>{trailing}</div>
        </Head>
      )}
      <Body>{children}</Body>
      {footer && <FooterArea>{footer}</FooterArea>}
    </Root>
  );
};

InspectorPanel.displayName = 'InspectorPanel';
